import type { ProductTaxonomy } from '@/services/product-taxonomy';
import { isHydraRoute } from '@/services/router/route-ownership';
import type { RouteOwnershipAccount } from '@/services/router/route-ownership';
import type { MouseEvent } from 'react';
import { AppBundle, AppBundleRoutes } from './app-bundles';

// URLs within apps will be of the form /workspaces/1234/journeys or
// /workspaces/1234/pipelines etc.
// This function regurns a regex for matching a URL with a given app.
function workspaceAppRegex(app: AppBundle) {
  // oxlint-disable-next-line local-rules/enforce-generate-path -- regex pattern, not a URL
  return new RegExp(`/workspaces/(last|(\\d+))${AppBundleRoutes[app]}`, 'i');
}

const WORKSPACE_SETTINGS_REGEX = /^\/workspaces\/(last|(\d+))\/settings/i;
const SETTINGS_REGEX = /^\/settings/i;

// There are a few special URLs that don't follow the above pattern. This
// contains regexes for matching those special cases.
const SPECIAL_URLS: [RegExp, AppBundle][] = [
  [WORKSPACE_SETTINGS_REGEX, AppBundle.Journeys],
  [SETTINGS_REGEX, AppBundle.Journeys],
  [/^\/internal-tools/i, AppBundle.Journeys],
  [/^\/logout/i, AppBundle.Journeys],
];

/**
 * Which embedded app (if any) serves this URL for this account. Route
 * ownership is flag-gated, so the account is a required input: call-time
 * consumers pass `accountStore.get()`; render-time consumers must pass an
 * account they're subscribed to (see `useActiveBundle`) so the
 * answer re-derives when the account loads.
 */
export function getAppBundleFromUrl(url: string, account: RouteOwnershipAccount): AppBundle | null {
  if (isHydraRoute(url, account)) {
    return null; // Hydra-only URLs are not associated with any app bundle
  }

  // Check for special URLs
  const matchBySpecialUrl = SPECIAL_URLS.find(([regex]) => url.match(regex) != null);
  if (matchBySpecialUrl != null) {
    return matchBySpecialUrl[1];
  }

  return Object.values(AppBundle).find((app) => url.match(workspaceAppRegex(app)) != null) ?? null;
}

/**
 * Reload the current page. Wrapped in a function so call sites can be tested
 * without triggering a real browser navigation.
 */
export function reloadWindow() {
  window.location.reload();
}

export type LinkClickEvent = Pick<
  MouseEvent,
  'button' | 'currentTarget' | 'altKey' | 'ctrlKey' | 'metaKey' | 'shiftKey'
>;

export function shouldProcessLinkClick(event: LinkClickEvent) {
  const isTargetBlank = event.currentTarget instanceof HTMLAnchorElement && event.currentTarget.target === '_blank';
  return event.button === 0 && !isTargetBlank && !event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey;
}

export function workspaceRootUrl(workspaceId: number | 'last' | null) {
  // oxlint-disable-next-line local-rules/enforce-generate-path -- dynamic fallback URL with nullable workspace ID
  return `/workspaces/${workspaceId ?? 'last'}/journeys`;
}

/** The workspace home screen — where a finished onboarding flow lands. */
export function workspaceHomeUrl(workspaceId: number) {
  // oxlint-disable-next-line local-rules/enforce-generate-path -- dynamic workspace URL, same shape as workspaceRootUrl above
  return `/workspaces/${workspaceId}/home`;
}

/**
 * Given a workspace and the current URL path, compute the best URL to navigate
 * to in the new workspace. This works by replacing the workspace ID and then
 * walking up the path segments, stripping any trailing segments that look like
 * dynamic IDs (numeric values, UUIDs, etc.) so that the user lands on the
 * nearest parent listing/index page rather than always being dumped to the
 * dashboard.
 *
 * For example:
 *   /workspaces/123/journeys/segments/456       → /workspaces/789/journeys/segments
 *   /workspaces/123/journeys/campaigns/456/overview → /workspaces/789/journeys/campaigns
 *   /workspaces/123/journeys/segments            → /workspaces/789/journeys/segments
 *   /workspaces/123/pipelines/sources/456        → /workspaces/789/pipelines/sources
 *   /workspaces/123/journeys/composer/newsletters/56 → /workspaces/789/journeys/broadcasts
 *
 * The embedded app's message editors are the exception to the walk-up — see
 * `COMPOSER_SECTION_LISTS`.
 *
 * `taxonomy` names the sections whose URL segment renames with the product taxonomy
 * (campaigns → automations). Render-time callers pass `useProductTaxonomy()` so the answer
 * re-derives when the account loads. It stays optional, and a type-only import, so this
 * module keeps to the `utils/` dependency direction and never pulls a service in at
 * runtime; omitted, the pre-rename vocabulary applies, which every account still resolves
 * (the embedded app registers the other spelling as a redirect).
 */
/**
 * The pages this app serves directly under a workspace, one segment long.
 *
 * Every migrated section adds its own here, and a section that forgets simply sends a workspace
 * switch to the workspace root instead of to the page — which is the harmless direction.
 */
const WORKSPACE_INDEX_SEGMENTS: ReadonlySet<string> = new Set(['imports', 'profiles']);

function isWorkspaceIndexSegment(segment: string | undefined): boolean {
  return segment != null && WORKSPACE_INDEX_SEGMENTS.has(segment.toLowerCase());
}

function journeysSectionUrl(workspaceId: number | 'last', segment: string): string {
  // oxlint-disable-next-line local-rules/enforce-generate-path -- embedded app URL built from a workspace id
  return `/workspaces/${workspaceId}${AppBundleRoutes[AppBundle.Journeys]}/${segment}`;
}

type ComposerSectionListUrl = (workspaceId: number | 'last', taxonomy: ProductTaxonomy | undefined) => string;

/**
 * Where a workspace switch lands when the reader is in one of the embedded app's message
 * editors, which all sit under `/journeys/composer/<type>/<id>…` (the `composer` block of
 * the app's router).
 *
 * Walking the ids off a composer URL leaves `/journeys/composer/<type>`, which is not a
 * page: the app matches it only with the composer's wildcard `fallback` route, which
 * redirects to the dashboard and hands back to Hydra's home. So switching workspaces from
 * an editor dropped the reader on Home, losing the section they were in. Send them to that
 * section's own list in the target workspace instead — the same URL the main navigation
 * links to.
 *
 * Keyed by the leading segment of each composer child route. A type absent here — including
 * anything the router's own `*:` fallback catches — falls back to the workspace root, the
 * same place the fallback route ends up: no list at all beats a guessed one, and either way
 * the reader must not be sent to the composer URL itself, which answers no page.
 */
const COMPOSER_SECTION_LISTS = new Map<string, ComposerSectionListUrl>([
  // `actions/:action_id` edits one automation's action, so its list is the automations
  // index — whose segment renames with the product taxonomy, hence the route lookup.
  // Without a taxonomy, the pre-rename segment: every account still resolves it (the
  // embedded app registers the renamed spelling as a redirect), so it costs a redirect hop
  // rather than the page. Render-time callers pass `useProductTaxonomy()` to skip the hop.
  [
    'actions',
    (workspaceId, taxonomy) =>
      taxonomy == null
        ? journeysSectionUrl(workspaceId, 'campaigns')
        : taxonomy.route('campaigns.index').replace(':workspaceId', String(workspaceId)),
  ],
  // `newsletters/:newsletter_id` edits a one-time send, which lives under Broadcasts.
  ['newsletters', (workspaceId) => journeysSectionUrl(workspaceId, 'broadcasts')],
  // `transactional/:transactional_message_id`, and its nested `templates/:template_id`.
  ['transactional', (workspaceId) => journeysSectionUrl(workspaceId, 'transactional')],
]);

function composerSectionListUrl(
  workspaceId: number | 'last',
  segments: string[],
  taxonomy: ProductTaxonomy | undefined,
): string | null {
  const [bundle, composer, composerType] = segments.map((segment) => segment.toLowerCase());

  if (bundle !== AppBundle.Journeys || composer !== 'composer') {
    return null;
  }

  if (composerType == null) {
    return workspaceRootUrl(workspaceId);
  }

  return COMPOSER_SECTION_LISTS.get(composerType)?.(workspaceId, taxonomy) ?? workspaceRootUrl(workspaceId);
}

export function workspaceUrlFromCurrentPath(
  workspaceId: number | 'last',
  currentPath: string,
  taxonomy?: ProductTaxonomy,
): string {
  // Match /workspaces/:id/... pattern and replace the workspace ID
  const workspacePathMatch = currentPath.match(/^\/workspaces\/(last|\d+)(\/.*)?$/i);

  if (workspacePathMatch == null) {
    // Current path doesn't match workspace pattern, fall back to root
    return workspaceRootUrl(workspaceId);
  }

  const restOfPath = workspacePathMatch[2] ?? '';
  const segments = restOfPath.split('/').filter((s) => s.length > 0);

  // A composer URL has no parent page to walk up to, so it answers for itself first.
  const composerUrl = composerSectionListUrl(workspaceId, segments, taxonomy);
  if (composerUrl != null) {
    return composerUrl;
  }

  // Walk up the parent routes: keep removing the last segment as long as any
  // remaining segment (after the app bundle) is a dynamic ID. This ensures
  // we don't land on a sub-route of a resource that doesn't exist in the
  // target workspace (e.g. /campaigns/456/overview → /campaigns).
  while (segments.length > 1 && segments.some((s, index) => index > 0 && isDynamicSegment(s, segments[index - 1]))) {
    segments.pop();
  }

  // One segment left is only a destination if it names a page this app serves at the workspace
  // root. A migrated URL drops the `journeys` segment, so those pages are one segment each, and
  // keeping them is what keeps the reader where they were. Everything else that walks up to a
  // single segment falls back: a bare app prefix ("/journeys", "/pipelines") names an app rather
  // than a page, and a deeper migrated URL can walk up to a segment nothing serves — the
  // workflow builder's `/automations/:id/workflow` leaves `/automations`, whose list still lives
  // at `/journeys/automations`, so keeping it would land the reader on a not-found page.
  //
  // An allow-list rather than a deny-list, because the failure directions are not equal: a
  // migrated page missing from it costs the reader their place, and a page wrongly kept in it
  // costs them a 404.
  if (segments.length === 0 || (segments.length === 1 && !isWorkspaceIndexSegment(segments[0]))) {
    return workspaceRootUrl(workspaceId);
  }

  // oxlint-disable-next-line local-rules/enforce-generate-path -- dynamically constructed from path segments
  return `/workspaces/${workspaceId}/${segments.join('/')}`;
}

// Route segments where the immediately following segment is always a dynamic
// slug (e.g. /objects/accounts where "accounts" is a custom object type slug).
const DYNAMIC_CHILD_SEGMENTS = new Set(['objects']);

/**
 * Returns true if a path segment looks like a dynamic ID rather than a named
 * route segment. Any segment containing a digit is treated as dynamic, since
 * dynamic segments come in many forms: pure numeric IDs ("123"), UUIDs,
 * alphanumeric IDs ("ob0101f2b501"), or even named slugs that include numbers.
 *
 * Some route segments (like "objects") are known to always be followed by a
 * dynamic slug (e.g. the object type name), so their children are also treated
 * as dynamic even if they contain no digits.
 */
function isDynamicSegment(segment: string, previousSegment?: string): boolean {
  // Segments immediately after known parent routes are always dynamic slugs
  if (previousSegment != null && DYNAMIC_CHILD_SEGMENTS.has(previousSegment)) {
    return true;
  }

  // Any segment containing a digit (numeric IDs, alphanumeric IDs, slugs with numbers, etc.)
  if (/\d/.test(segment)) {
    return true;
  }

  // UUIDs can technically be all letters (e.g. "abcdefab-abcd-abcd-abcd-abcdefabcdef")
  if (/^[\da-f]{8}(?:-[\da-f]{4}){3}-[\da-f]{12}$/i.test(segment)) {
    return true;
  }

  return false;
}
